import { darken, transparentize } from 'polished'
import {
  paletteTokens,
  typographyTokens,
  spacingTokens,
  scaleTokens,
  borderTokens,
  elevationTokens,
  iconTokens,
  breakpointTokens,
} from './tokens'

// Component-specific tokens
export const componentTokens = {
  button: {
    common: {
      borderRadius: borderTokens.radius.md,
      fontWeight: typographyTokens.weight.semibold,
      transition: 'all 0.2s ease-in-out',
    },
    primary: {
      backgroundColor: paletteTokens.primary.default,
      color: paletteTokens.text.alternate,
      border: 'none',
      hover: {
        backgroundColor: darken(0.1, paletteTokens.primary.default),
        color: paletteTokens.text.alternate,
      },
      active: {
        backgroundColor: darken(0.2, paletteTokens.primary.default),
        color: paletteTokens.text.alternate,
      },
    },
    secondary: {
      backgroundColor: 'transparent',
      color: paletteTokens.primary.default,
      border: `${borderTokens.width.sm} solid ${paletteTokens.primary.default}`,
      hover: {
        backgroundColor: transparentize(0.9, paletteTokens.primary.default),
        color: paletteTokens.primary.default,
        border: `${borderTokens.width.sm} solid ${darken(0.1, paletteTokens.primary.default)}`,
      },
      active: {
        backgroundColor: transparentize(0.8, paletteTokens.primary.default),
        color: paletteTokens.primary.default,
        border: `${borderTokens.width.sm} solid ${darken(0.2, paletteTokens.primary.default)}`,
      },
    },
    tertiary: {
      backgroundColor: 'transparent',
      color: paletteTokens.primary.default,
      border: 'none',
      textDecoration: 'underline',
      hover: {
        backgroundColor: transparentize(0.9, paletteTokens.primary.default),
        color: paletteTokens.primary.default,
        textDecoration: 'none',
      },
      active: {
        backgroundColor: transparentize(0.8, paletteTokens.primary.default),
        color: paletteTokens.primary.default,
        textDecoration: 'none',
      },
    },
    danger: {
      backgroundColor: paletteTokens.status.critical,
      color: paletteTokens.text.alternate,
      border: 'none',
      hover: {
        backgroundColor: darken(0.1, paletteTokens.status.critical),
        color: paletteTokens.text.alternate,
      },
      active: {
        backgroundColor: darken(0.2, paletteTokens.status.critical),
        color: paletteTokens.text.alternate,
      },
    },
    size: {
      sm: {
        height: spacingTokens[60], // 24px
        padding: `${spacingTokens[10]} ${spacingTokens[30]}`, // 4px 12px
        fontSize: typographyTokens.body.size.sm,
      },
      md: {
        height: spacingTokens[80], // 32px
        padding: `${spacingTokens[20]} ${spacingTokens[40]}`, // 8px 16px
        fontSize: typographyTokens.body.size.md,
      },
      lg: {
        height: spacingTokens[100], // 40px
        padding: `${spacingTokens[30]} ${spacingTokens[50]}`, // 12px 20px
        fontSize: typographyTokens.body.size.lg,
      },
    },
  },
  inputField: {
    backgroundColor: paletteTokens.background.default,
    border: `${borderTokens.width.sm} solid ${paletteTokens.border.default}`,
    borderRadius: borderTokens.radius.md,
    color: paletteTokens.text.default,
    fontSize: typographyTokens.body.size.md,
    padding: `${spacingTokens[30]} ${spacingTokens[40]}`,
    placeholder: {
      color: paletteTokens.neutral.emphasis,
    },
    focus: {
      borderColor: paletteTokens.primary.default,
      outline: `${borderTokens.width.md} solid ${transparentize(0.8, paletteTokens.primary.default)}`,
      outlineOffset: '0px',
    },
    error: {
      borderColor: paletteTokens.status.critical,
      color: paletteTokens.status.critical,
    },
    disabled: {
      backgroundColor: paletteTokens.disabled.muted,
      borderColor: paletteTokens.disabled.default,
      color: paletteTokens.disabled.emphasis,
    },
  },
  topbar: {
    backgroundColor: paletteTokens.background.default,
    borderBottom: `${borderTokens.width.sm} solid ${paletteTokens.border.default}`,
    height: spacingTokens[140], // 56px
    padding: `${spacingTokens[40]} ${spacingTokens[60]}`,
    color: paletteTokens.text.default,
  },
  rail: {
    backgroundColor: paletteTokens.background.default,
    border: `${borderTokens.width.sm} solid ${paletteTokens.border.default}`,
    width: spacingTokens[160], // 64px expanded, can be customized
    padding: spacingTokens[40],
    linkColor: paletteTokens.text.default,
    linkHover: {
      backgroundColor: paletteTokens.background.emphasis,
      color: paletteTokens.primary.default,
    },
  },
  card: {
    backgroundColor: paletteTokens.background.default,
    border: `${borderTokens.width.sm} solid ${paletteTokens.border.default}`,
    borderRadius: borderTokens.radius.lg,
    padding: spacingTokens[60],
    boxShadow: elevationTokens.bottom.sm,
  },
  interaction: {
    focus: {
      outline: `${borderTokens.width.md} solid ${paletteTokens.focus.default}`,
      outlineOffset: borderTokens.width.sm,
    },
  },
  input: {
    borderRadius: borderTokens.radius.md,
    transition: 'all 0.2s ease-in-out',
    size: {
      sm: {
        height: spacingTokens[80], // 32px
        padding: `${spacingTokens[20]} ${spacingTokens[30]}`, // 8px 12px
        fontSize: typographyTokens.body.size.sm,
      },
      md: {
        height: spacingTokens[100], // 40px
        padding: `${spacingTokens[30]} ${spacingTokens[40]}`, // 12px 16px
        fontSize: typographyTokens.body.size.md,
      },
      lg: {
        height: spacingTokens[120], // 48px
        padding: `${spacingTokens[40]} ${spacingTokens[50]}`, // 16px 20px
        fontSize: typographyTokens.body.size.lg,
      },
    },
  },
} as const

// Complete theme object
export const theme = {
  palette: paletteTokens,
  typography: typographyTokens,
  spacing: spacingTokens,
  scale: scaleTokens,
  border: borderTokens,
  elevation: elevationTokens,
  icon: iconTokens,
  breakpoint: breakpointTokens,
  component: componentTokens,
} as const

export type VulcanTheme = typeof theme
export type Theme = VulcanTheme